import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type { HandoutPreviewData, ReviewDiscussionData } from '@/types/review/ReviewDiscussion';

/**
 * The Prepare Meeting handout stores manager edits as "annotations" on the ReviewDiscussion
 * (`handoutAnnotations`, a JSON string). These are handout-only overrides used to render the downloaded
 * PDF — they never change the real assessment data the Results page reads. Several parts of the handout
 * write different keys into the SAME blob (e.g. `companyValues`, `performanceScore`), so every writer must
 * read-modify-write to preserve the other keys.
 */
export const mergeHandoutAnnotations = (
    existingAnnotations: string | undefined,
    patch: Record<string, unknown>,
): string => {
    let base: Record<string, unknown> = {};
    if (existingAnnotations) {
        try {
            const parsed = JSON.parse(existingAnnotations);
            base = Array.isArray(parsed) ? {} : (parsed as Record<string, unknown>);
        } catch {
            // Ignore invalid or legacy annotations and start from an empty object.
        }
    }
    return JSON.stringify({ ...base, ...patch });
};

/**
 * Resolve the manager name to display on the Prepare Meeting handout.
 *
 * Priority:
 * 1. HandoutAnnotations override (managerName / ManagerName) on the ReviewDiscussion
 * 2. Handout preview payload's managerName
 * 3. Fallback: empty string (the assessed employee and cycle instance remain the sources of truth
 *    for everything else; `assignedTo` is the reviewee, not the manager).
 */
export const getResolvedManagerName = (
    cycleInstance?: CycleInstance,
    reviewDiscussion?: ReviewDiscussionData,
    handoutData?: HandoutPreviewData,
): string => {
    const annotations = reviewDiscussion?.handoutAnnotations;
    if (annotations) {
        try {
            const parsed = JSON.parse(annotations) as unknown;
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                const obj = parsed as Record<string, unknown>;
                const rawFromCamel =
                    typeof obj.managerName === 'string' ? obj.managerName : undefined;
                const rawFromPascal =
                    typeof obj.ManagerName === 'string' ? obj.ManagerName : undefined;
                const raw = (rawFromCamel ?? rawFromPascal) as string | undefined;
                const trimmed = raw?.trim();
                if (trimmed) {
                    return trimmed;
                }
            }
        } catch {
            // Ignore invalid or legacy annotations when resolving the manager override.
        }
    }

    const fromHandout = handoutData?.managerName?.trim();
    if (fromHandout) {
        return fromHandout;
    }

    // There is no dedicated manager field on CycleInstance for this flow — `assignedTo` is the assessee.
    // Keep the fallback explicit so future extensions (e.g. a dedicated manager entity) have a clear hook.
    void cycleInstance; // currently unused, kept for future-proofing the signature

    return '';
};
